import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { useAppMessagePublication } from '@common/context/appMessagesContext';
import { useUserState } from '@common/context/userContext';
import AppMessages from '@components/AppMessages';
import Loader from '@components/Loader';
import PageLayout from '@components/PageLayout';
import ErrorBoundary from '@ds/components/feedback/ErrorBoundary';
import useAppNavigate from '@effects/useAppNavigate';
import useUserDetails from '@effects/useUserdetails';
import { useWebRouteKey, WebRoutePathContext } from '@effects/useWebRouteKey';
import { resolveMountCriteria } from '@utils/app-message-matcher';
import { setTokenProvider } from '@utils/axios-client';
import { AccountTypeEnum } from '@utils/enums';
import { reportBoundaryError } from '@utils/report-boundary-error';
import { format } from 'date-fns';
import { ComponentType, FC, useEffect, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

interface ProtectedRouteProps {
    /** The page to render once the user is signed in. */
    component: ComponentType;
    /** The route pattern this page is mounted at (e.g. `/register`); the new-user redirect skips that page. */
    path: string;
}

/**
 * The element for a route that needs a signed-in user: use as `<Route path="…" element={<ProtectedRoute … />} />`.
 * It is a plain element rather than a `<Route>` because React Router v6's `<Routes>` only accepts `<Route>` children.
 */
export const ProtectedRoute = ({ component, path }: ProtectedRouteProps) => {
    // Build the auth-wrapped component once per (component, path) pair. Doing this
    // inline on every render produced a new component identity each time, which made
    // React unmount/remount the routed page on param-only navigation (e.g. cycling
    // between Wizard steps), re-running its queries from scratch. returnTo is a
    // function so the post-login redirect still uses the current path despite memoising.
    const MyProtectedComponent = useMemo(() => {
        const RegisteredComponent = withUserDetails(component as ComponentType, path);
        return withAuthenticationRequired(RegisteredComponent as ComponentType, {
            onRedirecting: () => <Loader message="Signing you in…" />,
            returnTo: () => window.location.pathname,
        });
    }, [component, path]);

    return (
        <WebRoutePathContext.Provider value={path}>
            <MyProtectedComponent />
        </WebRoutePathContext.Provider>
    );
};

export const AdminProtectedRoute = ({ component, path }: ProtectedRouteProps) => {
    const [{ user }] = useUserState();
    if (user?.claims?.accounts?.some((acc) => acc.accountType === AccountTypeEnum.ADMIN))
        return (
            <ProtectedRoute
                component={component}
                path={path}
            />
        );
    else {
        return (
            <Navigate
                to="/"
                replace
            />
        );
    }
};

const withUserDetails = <P extends object>(Component: ComponentType<P>, path: any): FC<P> => {
    return function WithUserDetails(props: P): JSX.Element {
        const { isAuthenticated, getAccessTokenSilently, logout, isLoading: isFetchingAccessToken } = useAuth0();
        const { isLoading, isNewUser, fetchUserDetails } = useUserDetails();
        const [{ user }, dispatch] = useUserState();
        const navigate = useAppNavigate();
        const location = useLocation();

        // ENVDB-7836: the ONE place the App_Messages banner renders. This wrapper knows the route but
        // holds no consignment; pages that do (the wizard, summary, copy, device movement) publish
        // their forms and status through AppMessagesProvider, and resolveMountCriteria folds them in
        // when the publication is for this same route. Everywhere else matches on route, platform
        // and dates alone, so those rows must leave Forms and Show_For_Status blank in Airtable.
        const route = useWebRouteKey();
        const publication = useAppMessagePublication();
        const messages = (
            <AppMessages criteria={resolveMountCriteria(route, publication, format(new Date(), 'yyyy-MM-dd'))} />
        );

        // ENVDB-9411: a page that fails to render shows the boundary's fallback in place of the page, with the
        // Header (from PageLayout) still usable. Keyed on the path so navigating away clears a caught error.
        const page = (
            <ErrorBoundary
                resetKeys={[location.pathname]}
                onError={reportBoundaryError}
            >
                <Component {...props} />
            </ErrorBoundary>
        );

        if (isNewUser || user?.claims?.isNewUser) {
            if (path !== '/register') navigate('/register');
        }
        const isRegisterPage = window.location.pathname === '/register';

        useEffect(() => {
            // set up token provider for axios interceptor
            setTokenProvider(getAccessTokenSilently);
        }, [getAccessTokenSilently]);

        useEffect(() => {
            /**
             * isLoading is the flag for checking if userDetailsQuery request is processing
             * isFetchingAccessToken is the flag for checking if SSO request to MyMLA is processing
             * Only render the component and/or check the user's login status when both requests are
             * not processing
             */
            if (isLoading || isFetchingAccessToken) {
                return;
            }

            (async (): Promise<void> => {
                var accessToken;
                try {
                    accessToken = await getAccessTokenSilently();
                } catch (e) {
                    console.error('Something is wrong. no access token returned.');
                    logout({ logoutParams: { returnTo: window.location.origin + '/logout' } });
                }

                if (!accessToken) {
                    console.error('no access token returned.');
                    logout({ logoutParams: { returnTo: window.location.origin + '/logout' } });
                }

                if (accessToken) {
                    if (accessToken !== user?.token) {
                        // Signin call
                        await fetchUserDetails(accessToken);
                    }
                }

                if (!isLoading && isNewUser) {
                    if (path !== '/register') navigate('/register');
                }
            })();
        }, [
            isLoading,
            isFetchingAccessToken,
            fetchUserDetails,
            isNewUser,
            navigate,
            user?.token,
            dispatch,
            getAccessTokenSilently,
            logout,
        ]);

        return isFetchingAccessToken || isLoading || (isAuthenticated && !user.token) ? (
            <Loader />
        ) : isRegisterPage ? (
            <>
                {messages}
                {page}
            </>
        ) : (
            <PageLayout>
                {messages}
                {page}
            </PageLayout>
        );
    };
};
